import * as React from 'react';
import * as _ from 'lodash';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, IAppState, IMicrositeResponse, MicrositeActionCreators } from '@nkr/store/index';
import { IBaseResponse, IMicrositeRecipient } from '@nkr/models/index';
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, Panel, Row } from 'react-bootstrap';
import MicrositeHistory from './microsite-history';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

interface IProps {
    micrositeId: number,
    centerId: number,
    isAdmin: boolean,
    previousUrl: string,
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getMicrosite: (id: number, forCoach: boolean, confirm: boolean) => Promise<IMicrositeResponse>,
    deny: (id: number, editKey: string, reason: string) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    recipient: IMicrositeRecipient,
    previousLocation: string,
    reason: string,
    reasonValid: boolean,
    dirty: boolean
}

class MicrositeDeny extends React.Component<TProps, IState> {
    readonly state: IState = {
        recipient: null,
        previousLocation: '',
        reason: '',
        reasonValid: true,
        dirty: false
    }

    componentDidMount = () => {
        let previousLocation = this.props.isAdmin ? `/admin/microsite/${this.props.previousUrl}` : `/center/${this.props.centerId}/microsite/${this.props.previousUrl}`;

        this.props.showLoading('Getting the microsite, one moment please...').then(() => {
            this.props.getMicrosite(this.props.micrositeId, false, true)
                .then(response => {
                    if (response.data.success) {
                        this.setState({
                            recipient: response.data.results,
                            previousLocation
                        });
                    }

                    this.props.hideLoading();
                })
                .catch((error: AxiosError) => {
                    this.props.hideLoading();
                });
        });
    }

    handleReasonChange = (e: React.ChangeEvent<FormControl & HTMLTextAreaElement>) => {
        this.setState({
            reason: e.currentTarget.value
        });
    }

    handleDecline = () => {
        this.setState({
            reasonValid: this.state.reason != '',
            dirty: true
        }, () => {
            if (this.state.reasonValid) {
                let { id, editKey } = this.state.recipient;
                let {  reason } = this.state;

                this.props.showLoading('Denying self-registered microsite, one moment please...').then(() => {
                    this.props.deny(id, editKey, reason)
                        .then(response => {
                            this.props.hideLoading();

                            if (response.data.success) {
                                toast.success('Microsite denied successfully, going back to previous page', {
                                    onClose: () => {
                                        window.location.href = this.state.previousLocation;
                                    }
                                });
                            }
                        })
                        .catch((error: AxiosError) => {
                            toast.error('Unable to deny the microsite, the following error has been recorded: ' + error.message);
                        });
                });
            }
        });
    }

    handleCancel = () => {
        window.location.href = this.state.previousLocation;
    }

    render = () => {
        if (this.state.recipient == null)
            return <div>One moment please...</div>;

        const { currentStatusId, submissionLastIssue, fullName, logs } = this.state.recipient;

        return (
            <>
                <div className='container'>
                    {this.state.dirty && !this.state.reasonValid && (
                        <Alert bsStyle='danger'>Please correct the issues marked below...</Alert>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginBottom: 20 }}>
                        <h1 style={{ margin: 0 }}>Mark as Declined</h1>
                        <div className="pull-right">
                            <Button bsStyle='link' onClick={this.handleCancel}>Return to Previous List</Button>
                        </div>
                    </div>

                    {submissionLastIssue != '' && (
                        <Row>
                            <Col xs={12}>
                                <MicrositeHistory
                                    name={fullName}
                                    logs={logs}
                                    isExpanded={false}
                                />
                            </Col>
                        </Row>
                    )}

                    <Panel>
                        <Panel.Body>
                            <FormGroup validationState={this.state.dirty && !this.state.reasonValid ? 'error' : null}>
                                <Col xs={12} sm={2} componentClass={ControlLabel}>
                                    Reason for Denying the Microsite...
                                </Col>
                                <Col xs={12} sm={9}>
                                    <FormControl
                                        componentClass='textarea'
                                        cols={40}
                                        rows={10}
                                        style={{ height: 200, resize: 'none' }}
                                        value={this.state.reason}
                                        onChange={this.handleReasonChange}
                                    />
                                </Col>
                            </FormGroup>
                        </Panel.Body>
                    </Panel>

                    <FormGroup>
                        <Col xs={12} className="text-center">
                            <Button bsStyle='primary' bsSize='lg' onClick={this.handleDecline}>Mark Denied</Button>
                            <Button bsStyle='link' name='cancel' onClick={this.handleCancel}>Cancel Deny</Button>
                        </Col>
                    </FormGroup>
                </div>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => { return dispatch(AppActionCreators.showLoadingAsync(message)); },
        hideLoading: () => { return dispatch(AppActionCreators.hideLoading()); },
        getMicrosite: (id, forCoach, confirm) => { return dispatch(MicrositeActionCreators.getMicrosite(id, forCoach, confirm)); },
        deny: (id, editKey, reason) => { return dispatch(MicrositeActionCreators.deny(id, editKey, reason)); }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(MicrositeDeny);